import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import ImagePreviewModal from "./ImagePreviewModal";
import TemplateFilters from "./TemplateFilters";
import TemplateSearch from "./TemplateSearch";
import TemplateTable from "./TemplateTable";
import TemplatePagination from "./TemplatePagination";
import { Container, Box, Typography, Avatar } from "@mui/material";
import customer_avatar from "../../assets/images/customer_avatar.png";
import "./TemplateNew.css";
import Header from "../Header/Header";
import { getApi } from "../AppApis";
import { template } from "lodash";

const PAGE_SIZE = 10;

const Templates = () => {
    const navigate = useNavigate();

    const [token, setToken] = useState(localStorage.getItem("authUser"));
    const [selectedType, setSelectedType] = useState("All");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFilterType, setSelectedFilterType] = useState("all");
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [searchedTemplates, setSearchedTemplates] = useState([]);
    const [selectedFilterFolder, setSelectedFilterFolder] = useState("All");
    const [searchText, setSearchText] = useState("");
    const [recentlyUsed, setRecentlyUsed] = useState(true);

    const [availableStates, setAvailableStates] = useState([
        { label: "All", value: "all" },
        { label: "Minnesota", value: "MN_Forms" },
        { label: "North Carolina", value: "NC_Forms" },
        { label: "Florida", value: "FL_Forms" },
        // { label: "Hawaii", value: "HI_Forms" },
    ]);

    const [profile, setProfile] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        role: "",
    });
    const [isFileLoading, setIsFileLoading] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [anchorEl, setAnchorEl] = useState(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState("");
    const [loading, setLoading] = useState("");

    const modalRef = useRef(null);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (state) => {
        setCurrentPage(1);
        if (state?.value) setSelectedFilterType(state?.value);
        if (state?.label) setSelectedType(state?.label);
        setAnchorEl(null);
    };

    const handleRedirectToContract = (file) => {
        navigate("/contract-generation/welcome/", {
            state: { documents: [file] },
        });
    };

    const searchTemplates = (data, searchTerm) => {
        const normalizeString = (str) => str.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

        const regex1 = new RegExp(normalizeString(searchTerm), "i");

        return data.filter((obj) => {
            const normalizedValue = normalizeString(obj?.name);
            return !searchTerm.length || regex1.test(normalizedValue);
        });
    };

    useEffect(() => {
        const accessToken = localStorage.getItem("authUser");
        if (accessToken) {
            const token = accessToken.replace(/^"|"$/g, "");
            setToken(token);
            const profileUrl = `${config.get("API_URL")}/user/profile`;

            fetch(profileUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
                .then((response) => response.json())
                .then((data) => {
                    setProfile({
                        firstName: data.first_name,
                        lastName: data.last_name,
                        email: data.email,
                        phone: data.phone,
                        role: data.role,
                    });
                    localStorage.setItem("userName", data.first_name + " " + data.last_name);
                })
                .catch((error) => {
                    console.error("Error fetching profile:", error);
                });
        }
    }, []);

    const handleSearch = (text) => {
        setSearchText(text);
        setSearchedTemplates(!text?.length ? filteredTemplates : searchTemplates(filteredTemplates, text));
        setCurrentPage(1);
    };

    const handleDownload = useCallback(async (item, index) => {
        setIsFileLoading(index);
        const path = `https://pdf.onmls.com/documents/${item.url}`;
        try {
            const response = await fetch(path);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            a.download = item.name;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading file:", error);
        } finally {
            setIsFileLoading(null);
        }
    }, []);

    const handleQuickView = async (item) => {
        const fileUrl = `${"https://pdf.onmls.com/documents/"}${item?.url}`;
        setPreviewImageUrl(fileUrl);
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                closeModal();
            }
        };

        if (modalIsOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [modalIsOpen]);

    useEffect(() => {
        document.title = `Templates | ${config.get("COMPANY_NAME")}`;
    }, []);

    const handlePageClick = (event, data) => {
        setCurrentPage(data);
    };

    const handleRecentlyUsedClick = () => {
        setRecentlyUsed(!recentlyUsed);
    };

    const fetchContractTemplates = useCallback(
        async (type) => {
            setIsLoading(true);
            let url = "/documents/templates?per_page=200";
            if ((type || selectedFilterType) !== "all") {
                url += "&type=" + (type || selectedFilterType);
            }
            try {
                const response = await getApi(url);
                if (response?.error === 401) {
                    // throw new Error(data.error);
                    navigate("/login");
                    setIsLoading(false);
                    return;
                }
                if (response) {
                    let filterData;
                    const data = response?.response?.data?.data;
                    setTemplates(data);
                    if (selectedFilterFolder === "All") {
                        filterData = data;
                        setFilteredTemplates(data);
                    } else {
                        setFilteredTemplates(data);
                    }

                    setSearchedTemplates(data);
                } else {
                    console.error("Error fetching templates:");
                }
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching templates:", error);
            } finally {
                setIsLoading(false);
            }
        },
        [selectedFilterFolder, recentlyUsed, token]
    );

    useEffect(() => {
        fetchContractTemplates(selectedFilterType);
    }, [selectedFilterType]);

    const renderHighlightedText = (text, highlight) => {
        if (!highlight) return text;

        const parts = text.split(new RegExp(`(${highlight})`, "gi"));
        return parts.map((part, index) =>
            part.toLowerCase() === highlight.toLowerCase() ? (
                <span key={index} style={{ backgroundColor: "yellow" }}>
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    let filterByProperty_type =
        selectedFilterFolder === "All" ? templates : templates.filter((item) => item.property_type.includes(selectedFilterFolder));

    const dataToShow = searchText?.length ? searchedTemplates : filterByProperty_type;

    return (
        <>
            <Container className="tw-p-0 tw-h-screen tw-overflow-hidden tw-text-white tw-bg-background_color" maxWidth="">
                <Header title="Contract Templates" />
                <Box className=" tw-p-5 ">
                    <Box className="template-box tw-w-full md:tw-mt-5">
                        <Box
                            className="topBar docs-content tw-flex tw-flex-col lg:tw-flex-row tw-gap-4 lg:tw-gap-0"
                            sx={{ display: "flex", justifyContent: "space-between", mb: 3, mt: { xs: 0, sm: 0, md: 5 } }}
                        >
                            <TemplateFilters
                                setCurrentPage={setCurrentPage}
                                selectedFilterFolder={selectedFilterFolder}
                                setSelectedFilterFolder={setSelectedFilterFolder}
                            />
                            <TemplateSearch
                                searchText={searchText}
                                setSearchText={setSearchText}
                                handleSearch={handleSearch}
                                selectedType={selectedType}
                                handleMenuClick={handleMenuClick}
                                handleMenuClose={handleMenuClose}
                                anchorEl={anchorEl}
                                availableStates={availableStates}
                                setCurrentPage={setCurrentPage}
                                handleRecentlyUsedClick={handleRecentlyUsedClick}
                                recentlyUsed={recentlyUsed}
                                setDataToShow={setSearchedTemplates}
                            />
                        </Box>

                        {/* Conditionally render the table or the "Oops" message */}
                        {/* {dataToShow?.length > 0 ? ( */}
                        <>
                            <div className=" tw-overflow-y-auto tw-min-h-[230px]">
                                <TemplateTable
                                    isLoading={isLoading}
                                    dataToShow={dataToShow}
                                    renderHighlightedText={renderHighlightedText}
                                    handleDownload={handleDownload}
                                    handleQuickView={handleQuickView}
                                    handleRedirectToContract={handleRedirectToContract}
                                    searchText={searchText}
                                    currentPage={currentPage}
                                />
                            </div>
                            <Box className="template-footer tw-mt-4 tw-flex tw-w-full tw-justify-center tw-mb-5 ">
                                <TemplatePagination
                                    currentPage={currentPage}
                                    pageCount={Math.ceil(dataToShow?.length / PAGE_SIZE)}
                                    handlePageClick={handlePageClick}
                                />
                            </Box>
                        </>

                        <div ref={modalRef}>
                            <ImagePreviewModal isOpen={modalIsOpen} onClose={closeModal} imageUrl={previewImageUrl} />
                        </div>
                        <br />
                    </Box>
                </Box>
            </Container>
        </>
    );
};

export default Templates;
